import { Injectable, Inject, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class TableEnhancementService {
  private observer?: MutationObserver;
  private enhancedTables = new WeakSet<HTMLElement>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {}

  initialize(): void {
    this.ngZone.runOutsideAngular(() => {
      // Initial enhancement of existing tables
      this.enhanceExistingTables();
      
      // Watch for new tables being added
      this.setupMutationObserver();
    });
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private enhanceExistingTables(): void {
    const tables = this.document.querySelectorAll('.p-datatable');
    tables.forEach(table => this.enhanceTable(table as HTMLElement));
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              
              // Check if the added element is a table
              if (element.classList?.contains('p-datatable')) {
                setTimeout(() => this.enhanceTable(element), 100);
              }
              
              // Check if the added element contains tables
              const tables = element.querySelectorAll?.('.p-datatable');
              if (tables) {
                tables.forEach(table => {
                  setTimeout(() => this.enhanceTable(table as HTMLElement), 100);
                });
              }
            }
          });
        }
      });
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true
    });
  }

  private enhanceTable(tableElement: HTMLElement): void {
    if (this.enhancedTables.has(tableElement)) {
      return; // Already enhanced
    }

    const wrapper = tableElement.querySelector('.p-datatable-wrapper') as HTMLElement;
    if (!wrapper) {
      // Try again later if the wrapper isn't ready yet
      setTimeout(() => this.enhanceTable(tableElement), 200);
      return;
    }

    // Mark as enhanced
    this.enhancedTables.add(tableElement);

    // Create top scrollbar elements
    const topScrollContainer = this.document.createElement('div');
    topScrollContainer.className = 'p-datatable-top-scroll';
    
    const topScrollContent = this.document.createElement('div');
    topScrollContent.className = 'p-datatable-top-scroll-content';
    
    topScrollContainer.appendChild(topScrollContent);

    // Insert at the top of the table content area (inside wrapper, before the actual table)
    wrapper.parentElement?.insertBefore(topScrollContainer, wrapper);

    // Setup scrollbar synchronization
    this.setupScrollSync(topScrollContainer, wrapper, topScrollContent);

    // Monitor table changes
    this.setupTableObserver(tableElement, topScrollContainer, topScrollContent, wrapper);
  }

  private setupScrollSync(topScrollContainer: HTMLElement, wrapper: HTMLElement, topScrollContent: HTMLElement): void {
    let isTopScrolling = false;
    let isTableScrolling = false;

    // Sync top scroll to table scroll
    topScrollContainer.addEventListener('scroll', () => {
      if (isTableScrolling) return;
      isTopScrolling = true;
      wrapper.scrollLeft = topScrollContainer.scrollLeft;
      setTimeout(() => isTopScrolling = false, 10);
    });

    // Sync table scroll to top scroll
    wrapper.addEventListener('scroll', () => {
      if (isTopScrolling) return;
      isTableScrolling = true;
      topScrollContainer.scrollLeft = wrapper.scrollLeft;
      setTimeout(() => isTableScrolling = false, 10);
    });

    // Update scrollbar visibility and width
    this.updateScrollbar(wrapper, topScrollContainer, topScrollContent);
  }

  private setupTableObserver(tableElement: HTMLElement, topScrollContainer: HTMLElement, topScrollContent: HTMLElement, wrapper: HTMLElement): void {
    // Use ResizeObserver if available
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        this.updateScrollbar(wrapper, topScrollContainer, topScrollContent);
      });
      resizeObserver.observe(wrapper);
    }

    // Also use MutationObserver for content changes
    const mutationObserver = new MutationObserver(() => {
      setTimeout(() => this.updateScrollbar(wrapper, topScrollContainer, topScrollContent), 100);
    });

    mutationObserver.observe(tableElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  private updateScrollbar(wrapper: HTMLElement, topScrollContainer: HTMLElement, topScrollContent: HTMLElement): void {
    const tableWidth = wrapper.scrollWidth;
    const containerWidth = wrapper.clientWidth;
    
    if (tableWidth > containerWidth) {
      topScrollContainer.style.display = 'block';
      topScrollContent.style.width = `${tableWidth}px`;
    } else {
      topScrollContainer.style.display = 'none';
    }
  }
}